import React from 'react';
import tw from 'twin.macro';
import { css } from '@emotion/react';
import { BtnPrimary, ShadowBox } from './scaffolds';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

const HowToItem: React.FC<{
  style?: React.CSSProperties;
  className?: string;
  currentIndex?: number;
  contents?: HowToContent[];
}> = ({ style, className, currentIndex = 0, contents = [] }) => (
  <div style={style} className={className}>
    <div tw="grid grid-cols-7 gap-0 lg:gap-2 relative mb-4 lg:mb-14">
      {contents.map((item, index) => (
        <div key={'howtoitem' + index} tw="col-span-7 lg:col-span-1">
          <div
            tw="text-b1 p-3 rounded-2xl flex flex-col items-center justify-center"
            css={css`
              min-height: 60px;
              ${index <= currentIndex ? tw`bg-blue-marine dark:bg-orange` : tw`bg-blue-sky`}
              ${index <= currentIndex ? tw`text-white dark:text-black-100` : tw`text-blue-marine`}
              @media (max-width: 1023px) {
                ${index === currentIndex ? 'block' : tw`hidden`}
              }
              @media (max-width: 1280px) {
                ${tw`text-b3`}
              }
            `}
          >
            <p tw="mb-0 text-center">{item.summary}</p>
          </div>
        </div>
      ))}
    </div>
    <div
      css={css`
        overflow: hidden;
      `}
    >
      <SwitchTransition mode="out-in">
        <CSSTransition<undefined>
          key={currentIndex}
          classNames="slide"
          addEndListener={(node: HTMLElement, done: () => void) => {
            node.addEventListener('transitionend', done, false);
          }}
        >
          <div
            tw="mx-4 lg:mx-0"
            css={css`
              @media (max-width: 1024px) {
                flex-wrap: wrap;
              }
            `}
          >
            <h3>{contents[currentIndex]?.title}</h3>
            <div tw="text-b1 mb-14">{contents[currentIndex]?.detail}</div>
          </div>
        </CSSTransition>
      </SwitchTransition>
    </div>
  </div>
);

const HowToItemProgress: React.FC<{
  style?: React.CSSProperties;
  className?: string;
  currentIndex?: number;
  length?: number;
}> = ({ style, className, currentIndex = 0, length = 0 }) => (
  <div style={style} className={className} tw="grid grid-cols-7 gap-2 relative">
    {[...Array(length)].map((_, index) => (
      <div key={'how-to-progress' + index} tw="relative">
        <div
          className="line"
          tw="absolute"
          css={css`
            top: 13px;
            height: 4px;
            width: 100%;
            z-index: 1;
            left: 50%;
            ${index < currentIndex ? tw`bg-blue-marine dark:bg-orange` : tw`bg-blue-sky`}
            ${index + 1 === length ? tw`hidden` : tw`block`}
          `}
        ></div>
        <div
          className="line"
          tw="absolute"
          css={css`
            top: 13px;
            height: 4px;
            width: 2vw;
            z-index: 1;
            left: 50%;
            ${index <= currentIndex ? tw`bg-blue-marine dark:bg-orange` : tw`bg-blue-sky`}
            ${index + 1 === length ? tw`hidden` : tw`block`}
          `}
        ></div>
        <div tw="flex justify-center">
          <div
            tw="rounded-full text-center p-2 mb-4"
            css={css`
              height: 32px;
              width: 32px;
              z-index: 2;
              ${index <= currentIndex ? tw`bg-blue-marine dark:bg-orange` : tw`bg-blue-sky`}
              ${index <= currentIndex ? tw`text-white dark:text-black-100` : tw`text-blue-marine`}
            `}
          >
            {index + 1}
          </div>
        </div>
      </div>
    ))}
  </div>
);

interface HowToProps {
  title?: any;
  subtitle?: any;
  howToLink?: string;
  onHowToLinkClicked?: any;
  buttonClass?: string;
}

interface HowToState {
  currentIndex?: number;
}

export interface HowToContent {
  title?: string;
  summary?: string;
  detail?: any;
  link?: string;
}

const contents: HowToContent[] = [
  {
    summary: 'Konsultasi',
    title: 'Konsultasikan kebutuhan PT Anda bersama Easybiz',
    detail: (
      <div>
        Anda akan diberikan arahan dan panduan secara lengkap untuk mempersiapkan kebutuhan
        pendirian PT Anda seperti pemilihan KBLI dan persiapan administrasi.
        <br />
        <br />
        Easybiz telah membantu lebih dari 2000 pendirian PT dan perizinan usaha sehingga kami sudah
        berpengalaman dalam memberikan konsultasi. Anda tidak perlu khawatir karena seluruh proses
        pendirian PT Anda ditangani oleh ahlinya.
      </div>
    ),
  },
  {
    summary: 'Pengumpulan Berkas',
    title: 'Easybiz Form membuat pengumpulan persyaratan menjadi praktis',
    detail: (
      <div>
        Easybiz telah mengembangkan Easybiz Form yang telah diriset sejak tahun 2015 dan terus
        diperbarui sesuai dengan regulasi terbaru untuk memudahkan pengisian informasi pendirian PT
        Anda.
        <br />
        <br />
        Easybiz Form disusun dengan akurat dan ringkas sehingga pengumpulan berkas persyaratan
        seperti KTP, NPWP, dan informasi lainnya menjadi lebih mudah dan tidak terlewat.
        <br />
        <br />
        Estimasi waktu pengerjaan 1 hari.
      </div>
    ),
  },
  {
    summary: 'Pengecekan Nama PT',
    title: 'Buat nama PT sesuai dengan keinginan Anda',
    detail: (
      <div>
        Banyak pengusaha berkata menentukan nama perusahaan rasanya seperti memberikan nama pada
        anak, penuh arti dan kesan. Segera ajukan beberapa nama PT yang Anda inginkan, kami akan
        langsung mengecek ketersediaannya di Kemenkumham dan langsung mengklaimnya untuk Anda.
        <br />
        <br />
        Estimasi waktu pengerjaan 1 hari.
      </div>
    ),
  },
  {
    summary: 'Pembuatan Akta dan SK',
    title: 'Deklarasi pendirian perusahaan Anda diabadikan dengan Akta dan SK Kemenkumham',
    detail: (
      <div>
        Sebagai dokumen terpenting dalam pendirian PT, Akta dan SK Kemenkumham harus dipastikan
        tepat dan valid. Tim kami akan berkomunikasi intensif dengan Anda untuk mewujudkan Drafting
        Akta yang paling sesuai dengan keinginan Anda. Koordinasi yang baik antara Easybiz dengan
        notaris akan membuat pengurusan Akta dan SK Kemenkumham selesai dengan sangat cepat.
        <br />
        <br />
        Seluruh pengurusan berkas baik pengiriman, pengambilan, dan penandatanganan dokumen akan
        dilayani secara “jemput bola” sehingga Anda hanya perlu duduk manis di rumah.
        <br />
        <br />
        Estimasi waktu pengerjaan 1 sampai 3 hari.
      </div>
    ),
  },
  {
    summary: 'NPWP Perusahaan',
    title: 'Memiliki NPWP perusahaan adalah hal krusial dalam aktivitas perusahaan Anda',
    detail: (
      <div>
        NPWP perusahaan sangat penting dimiliki sejak perusahaan Anda didirikan. NPWP adalah dokumen
        penting untuk legalitas usaha Anda yang akan digunakan untuk kewajiban pajak, penggajian
        karyawan, hingga pengembangan usaha.
        <br />
        <br />
        Kami memiliki tim yang terorganisir sehingga proses pembuatan NPWP dapat diselesaikan dengan
        cepat. Anda cukup duduk manis di rumah tanpa perlu ke Kantor Pelayanan Pajak. Kartu NPWP dan
        SKT (Surat Keterangan Terdaftar) yang sudah selesai akan dikirimkan ke alamat Anda.
        <br />
        <br />
        Estimasi waktu pengerjaan 1 sampai 3 hari.
      </div>
    ),
  },
  {
    summary: 'Pembuatan NIB dan OSS',
    title:
      'Nomor Induk Berusaha (NIB) dan Online Single Submission (OSS) sebagai nomor identitas di PT Anda',
    detail: (
      <div>
        Mendapatkan NIB dan Izin Usaha kini lebih mudah dengan adanya OSS. Namun, pengisian OSS yang
        harus dilakukan berdasarkan KBLI, membutuhkan pengetahuan khusus, pengalaman, dan kesabaran.
        Ada lebih dari 100 item informasi yang harus diisi di OSS.
        <br />
        <br />
        Tim Easybiz yang berpengalaman akan mengisikan formulir OSS untuk Anda sehingga pendaftaran
        NIB dan KBLI bisa lebih cepat. Berkat jam terbang yang sudah dimiliki, kami bisa memberikan
        solusi terbaik jika ada kendala dalam pengisian formulir OSS.
        <br />
        <br />
        Estimasi waktu pengerjaan 1 sampai 3 hari.
      </div>
    ),
  },
  {
    summary: 'Pendirian PT Selesai',
    title: 'Selamat, seluruh dokumen pendirian PT akan diserahkan kepada Anda',
    detail: (
      <div>
        Anda telah berhasil mendirikan PT yang resmi secara hukum dan memiliki dokumen yang lengkap.
        Easybiz akan menyerahkan Akta Salinan, SK Kemenkumham, Tambahan BNRI, Kartu NPWP, SKT, NIB,
        dan Izin Usaha langsung ke tempat Anda.
        <br />
        <br />
        Kini Anda bisa melakukan hal-hal berikut ini:
        <ul>
          <li tw="m-0">Membuka rekening bank perusahaan</li>
          <li tw="m-0">Ikut serta dalam tender pemerintah</li>
          <li tw="m-0">Mencari investor dan mendapatkan tambahan modal</li>
        </ul>
      </div>
    ),
  },
];

class HowTo extends React.Component<HowToProps, HowToState> {
  constructor(props: HowToProps) {
    super(props);
    this.state = {
      currentIndex: 0,
    };
  }

  left() {
    const idx = this.state?.currentIndex || 0;
    if (idx < 1) {
      return;
    }
    this.setState({
      currentIndex: idx - 1,
    });
  }

  right() {
    const idx = this.state?.currentIndex || 0;
    if (idx > 6) {
      return;
    }
    this.setState({
      currentIndex: idx + 1,
    });
  }

  render() {
    const totalData = 7;
    return (
      <section tw="bg-black-700 dark:bg-black-200">
        <div tw="container">
          <div tw="lg:text-center mx-8 lg:mx-32 lg:text-center py-12 lg:pb-28">
            <h2 id="proses" tw="text-black-100 dark:text-white mb-4">{this.props.title}</h2>
            <div tw="text-b1 mb-10 dark:text-white">{this.props.subtitle}</div>
            <ShadowBox>
              <div tw="absolute left-1 lg:left-4 h-full top-0 flex flex-col justify-center">
                <button
                  className={this.props.buttonClass || 'prev-button'}
                  tw="cursor-pointer bg-yellow-bright rounded-2xl border-0 py-2 px-3 lg:py-1 lg:px-2 focus:outline-none"
                  onClick={() => this.left()}
                  css={css`
                    ${!this.state?.currentIndex || this.state?.currentIndex < 1
                      ? tw`opacity-20 bg-black-700`
                      : ``}
                  `}
                  disabled={!this.state?.currentIndex || this.state?.currentIndex < 1}
                >
                  <span
                    tw="text-black-300 text-h4 lg:text-h2"
                    className="icon icon-chevron-left"
                    aria-label="slide-left"
                  ></span>
                </button>
              </div>
              <div tw="absolute right-1 lg:right-4 h-full top-0 flex flex-col justify-center">
                <button
                  className={this.props.buttonClass || 'next-button'}
                  tw="cursor-pointer bg-yellow-bright rounded-2xl border-0 py-2 px-3 lg:py-1 lg:px-2 focus:outline-none"
                  onClick={() => this.right()}
                  css={css`
                    ${this.state?.currentIndex > totalData - 2 ? tw`opacity-20 bg-black-700` : ``}
                  `}
                  disabled={this.state?.currentIndex > totalData - 2}
                >
                  <span
                    tw="text-black-300 text-h4 lg:text-h2"
                    className="icon icon-chevron-right"
                    aria-label="slide-right"
                  ></span>
                </button>
              </div>
              <div tw="mb-6 lg:mb-4">
                <HowToItemProgress
                  length={7}
                  currentIndex={this.state?.currentIndex}
                ></HowToItemProgress>
                <HowToItem contents={contents} currentIndex={this.state?.currentIndex}></HowToItem>
              </div>
              <p tw="text-b1 text-center mb-4">Punya pertanyaan?</p>
              <div tw="flex justify-center">
                <BtnPrimary
                  className="gtm-call-button"
                  href={this.props.howToLink || '/hubungi-kami-via-whatsapp'}
                  target="__blank"
                  onClick={this.props.onHowToLinkClicked}
                >
                  WhatsApp Kami Di Sini
                </BtnPrimary>
              </div>
            </ShadowBox>
          </div>
        </div>
      </section>
    );
  }
}

export default HowTo;
